import { getVolume, setVolumeLevel } from '../utils/volume';
/**
 * ReadingVox - Main Widget Class
 * Server TTS (Kokoro) with browser fallback
 */

import { detectContentLanguage, readingVoiceFor, type ReadingVoice } from '../utils/contentLanguage';
import type { ReadingVoxConfig, ReadingVoxDefaults, WordHighlightEvent } from '../types/config';
import { DEFAULT_CONFIG, normalizeVoice } from '../types/config';
import { APIClient, type StreamWindow } from '../utils/api';
import { TextExtractor, readableSelectionText } from '../utils/textExtractor';
import { watchDocumentLinks } from '../utils/documentLinks';
import { splitIntoSentences } from '../utils/sentences';
import { BrowserTTS } from './BrowserTTS';
import { StreamingPlayer } from './StreamingPlayer';
import type { FallbackSpeaker } from './StreamingPlayer';
import { CacheManager } from './CacheManager';
import type { PageMode } from '../modes/PageMode';
import type { BlockMode } from '../modes/BlockMode';
import { HighlightMode } from '../modes/HighlightMode';
import { ListenButtons, type PageListenState } from '../ui/ListenButton';
import { resolveTheme } from '../ui/ThemeManager';
import { loadExtras } from '../lazy';
import { WordHighlighter } from '../ui/WordHighlighter';
import { IS_WEB_BUILD } from '../env';
import { setDebug } from '../utils/logger';
import type { WidgetFeatureConfig } from '../types/api';
import { detectLMS, detectPreset, isCanvasStaff, isMoodleStaff, presetConfig, pageUrlForApi } from '../lms';
import type { LmsId } from '../lms';
import { ContentObserver } from '../utils/contentObserver';
import { FrameManager } from '../frames/FrameManager';
import { FrameAwareHighlighter } from '../frames/FrameAwareHighlighter';
import { listenForInvite, startFrameMode } from '../frames/frameMode';
import type { FrameRect } from '../frames/protocol';
import { t, resolveUiLanguage, loadUiLanguage } from '../i18n';
import { UI_STRINGS_VERSION } from '../i18n/version';

/** Features assumed when /widget/config can't be loaded: no translation, show branding */
/** Sentences generated per request, and how close playback gets before the next is asked for */
const STREAM_WINDOW = 20;
const STREAM_LOOKAHEAD = 8;

const FALLBACK_FEATURES: WidgetFeatureConfig = {
  branding: true,
  selectionTranslation: false,
  fullPageTranslation: false,
  languages: [],
};

export class ReadingVox {
  private config: ReadingVoxConfig & typeof DEFAULT_CONFIG;
  private apiClient: APIClient;
  private cacheManager: CacheManager;
  private textExtractor: TextExtractor;
  private browserTTS: BrowserTTS | null = null;
  private streamingPlayer: StreamingPlayer | null = null;
  private wordHighlighter: WordHighlighter | null = null;
  /** Reads iframes as part of the page (see src/widget/frames) */
  private frames: FrameManager | null = null;
  /** This copy runs inside a frame as the parent page's agent: no UI of its own */
  private frameAgentMode = false;
  private stopInviteListen: (() => void) | null = null;
  /** Listen buttons the site placed with data-readingvox-button */
  private listenButtons: ListenButtons | null = null;
  /** The streaming player is waiting for audio */
  private buffering = false;
  private currentMode: PageMode | BlockMode | HighlightMode | null = null;
  private isInitialized: boolean = false;
  private currentText: string = '';
  /** Last text read from the page itself (vs a selection or document), to spot page changes */
  private pageText: string = '';
  private isRestarting: boolean = false;
  private wordHighlightCallbacks: ((event: WordHighlightEvent) => void)[] = [];
  // Cached sentence split for BrowserTTS boundary events
  private _browserTTSSentences: string[] | null = null;
  private _browserTTSFullText: string = '';
  private _browserTTSSentenceOffsets: number[] = [];
  // Server feature flags (translation, branding) — fetched once, lazily
  private featuresPromise: Promise<WidgetFeatureConfig> | null = null;
  // Translation state ('en' = original page language)
  private language: string = 'en';
  private translationCache: Map<string, { translations: string[]; voice: string | null }> = new Map();
  private captionCallbacks: ((caption: string | null) => void)[] = [];
  private translatedSpeechActive: boolean = false;
  private playbackToken: number = 0;
  // True when the current streaming player is a one-off (selection translation "Listen")
  private streamingIsOneOff: boolean = false;
  // LMS detected on this page (website builds only; reporting / licensing hint)
  private lms: LmsId | null = null;
  private contentObserver: ContentObserver | null = null;
  private waitingForContent: boolean = false;
  private initStarted: boolean = false;
  private destroyed: boolean = false;
  private lmsNotice: { destroy(): void } | null = null;
  /** How the current text is being read (page language → voice), set by playText() */
  private readingVoice: ReadingVoice = { kind: 'default' };

  // Text the browser voice is reading (may be the remainder after a stream failure)
  private browserTTSText: string = '';

  /**
   * Default API base used when config.apiEndpoint is not given.
   * The website bundle sets this to `<script origin>/api/v1` so a relative
   * path never hits the publisher's own server.
   */
  static defaultApiEndpoint: string = DEFAULT_CONFIG.apiEndpoint;

  /** Live instances (used by auto-init to avoid double-initialising) */
  static instances: Set<ReadingVox> = new Set();
  private static instanceListeners: ((instance: ReadingVox) => void)[] = [];
  /** @internal Subscribe to instance creation (auto-init uses this) */
  static onInstanceCreated(cb: (instance: ReadingVox) => void): void {
    ReadingVox.instanceListeners.push(cb);
  }

  constructor(config: ReadingVoxConfig) {
    // Merge with defaults (drop undefined values so they don't clobber defaults)
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(config || {})) {
      if (v !== undefined) cleaned[k] = v;
    }

    // Platform preset: explicit, or auto-applied on Canvas / Moodle (website builds
    // only — the Chrome extension keeps its own behaviour unless a preset is passed)
    let preset = config?.preset;
    if (!preset && !config?.selectors && IS_WEB_BUILD) preset = detectPreset() || undefined;
    const presetValues = preset && preset !== 'none' ? presetConfig(preset) : {};

    this.config = {
      ...DEFAULT_CONFIG,
      apiEndpoint: ReadingVox.defaultApiEndpoint,
      ...presetValues,
      ...cleaned,
      ...(preset ? { preset } : {}),
    } as ReadingVoxConfig & ReadingVoxDefaults;

    if (IS_WEB_BUILD) this.lms = detectLMS();

    // Old voice ids (alloy, echo, nova, fable…) → current names
    this.config.voice = normalizeVoice(this.config.voice);

    // Strip trailing slash from API base
    this.config.apiEndpoint = this.config.apiEndpoint.replace(/\/+$/, '');

    // OCR / screenshot reader is an extension-only feature (tesseract.js is not
    // shipped in the website bundle — page text is already real text there).
    if (IS_WEB_BUILD) {
      this.config.enabledTools = this.config.enabledTools.filter(t => t !== 'screenshotReader');
    }

    if (this.config.debug) setDebug(true);

    // Validate authentication
    if (!this.config.apiKey && !this.config.licenseKey) {
      throw new Error('Either apiKey or licenseKey is required');
    }

    // Initialize components
    this.apiClient = new APIClient(
      { apiKey: this.config.apiKey, licenseKey: this.config.licenseKey, studentEmail: this.config.studentEmail },
      this.config.apiEndpoint,
      this.config.debug,
      { stripUrlParams: !!this.config.stripUrlParams, lms: this.lms }
    );

    this.cacheManager = new CacheManager(this.config.cacheSize);
    this.textExtractor = new TextExtractor(
      this.config.selectors,
      this.config.ignoreSelectors,
      { fallbackToBody: !this.config.contentOnly, blockExclude: this.config.blockExclude }
    );

    // Initialize browser TTS if supported
    if (BrowserTTS.isSupported()) {
      this.browserTTS = new BrowserTTS();

      // Wire word boundary events from BrowserTTS
      this.browserTTS.onBoundary((word, charIndex, charLength, fullText) => {
        // Use splitIntoSentences for robust sentence detection
        // (handles abbreviations like "Dr.", "U.S." correctly)
        if (!this._browserTTSSentences || this._browserTTSFullText !== fullText) {
          this._browserTTSSentences = splitIntoSentences(fullText);
          this._browserTTSFullText = fullText;
          // Build char-offset map for each sentence
          this._browserTTSSentenceOffsets = [];
          let offset = 0;
          for (const s of this._browserTTSSentences) {
            const idx = fullText.indexOf(s, offset);
            this._browserTTSSentenceOffsets.push(idx >= 0 ? idx : offset);
            offset = (idx >= 0 ? idx : offset) + s.length;
          }
        }

        // Binary search for which sentence contains charIndex
        let sentenceIdx = 0;
        for (let i = 0; i < this._browserTTSSentences.length; i++) {
          const start = this._browserTTSSentenceOffsets[i];
          const end = start + this._browserTTSSentences[i].length;
          if (charIndex >= start && charIndex < end) {
            sentenceIdx = i;
            break;
          }
          if (charIndex < start) break;
          sentenceIdx = i;
        }

        const sentence = this._browserTTSSentences[sentenceIdx];
        const sentenceStart = this._browserTTSSentenceOffsets[sentenceIdx];

        // charStart/charEnd relative to the sentence (WordHighlighter expects this)
        const charStartInSentence = charIndex - sentenceStart;
        const charEndInSentence = charStartInSentence + (charLength || word.length);

        // Drive word highlighter
        if (this.wordHighlighter) {
          this.wordHighlighter.highlightWord({
            word,
            timestamp: 0,
            charStart: charStartInSentence,
            charEnd: charEndInSentence,
            sentence,
          });
        }

        // Emit to toolbar tools (magnifier, screen mask)
        const wordsBeforeInSentence = fullText.substring(sentenceStart, charIndex).trim().split(/\s+/).filter(w => w.length > 0);
        this.emitWordHighlight({
          word,
          sentence,
          wordIndex: wordsBeforeInSentence.length,
          timestamp: 0,
        });

        this.config.onWordHighlight?.(word, 0);
      });
    }

    // Always initialize word highlighter (BrowserTTS now supports it too)
    this.wordHighlighter = new FrameAwareHighlighter(this.config.autoScroll);
    this.applyReadingSettings({});

    // Read iframes in place (same-origin directly, others via frame mode). Started
    // now so framed copies of the widget can say hello before init finishes.
    if (this.config.readFrames !== false && typeof window !== 'undefined') this.startFrames();

    this.log('ReadingVox initialized', { ...this.config, apiKey: undefined, licenseKey: undefined });

    ReadingVox.instances.add(this);
    ReadingVox.instanceListeners.forEach(cb => {
      try { cb(this); } catch { /* ignore */ }
    });
  }

  /**
   * Server feature flags (translation, branding, language list).
   * Never rejects — falls back to "no translation, show branding".
   *
   * Fetched lazily: nothing is requested from our servers on a page load. The
   * first time the visitor engages (opens the toolbar, gets the selection menu,
   * plays something, translates), this fetches /widget/config once.
   */
  getFeatures(): Promise<WidgetFeatureConfig> {
    if (!this.featuresPromise) {
      this.featuresPromise = this.apiClient.getWidgetConfig().catch((error) => {
        this.log('widget/config failed — translation disabled:', error);
        return FALLBACK_FEATURES;
      });
      this.featuresPromise.then(this.resolveFeatures);
    }
    return this.featuresPromise;
  }

  /** Resolves when the features are fetched — without causing the fetch */
  whenFeatures(): Promise<WidgetFeatureConfig> {
    return this.featuresReady;
  }

  private resolveFeatures!: (f: WidgetFeatureConfig) => void;
  private featuresReady: Promise<WidgetFeatureConfig> = new Promise((r) => { this.resolveFeatures = r; });

  /** Toolbar / selection menu UI announces the visitor's first interaction */
  private onEngaged = () => { void this.getFeatures(); };

  private stopDocumentLinks: (() => void) | null = null;
  private documentReader: { destroy(): void } | null = null;

  /**
   * Open a PDF in the reader view and read it aloud, with highlighting in the
   * reader. `opener` gets focus back when the reader closes.
   */
  async readDocument(url: string, opener?: HTMLElement | null): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize().catch(() => {});
      if (!this.isInitialized) return;
    }
    this.stop();
    this.closeDocument();
    const { DocumentReader } = await loadExtras();
    const reader = new DocumentReader({
      url,
      returnFocus: opener ?? null,
      onClose: () => this.closeDocument(),
      onPlayPause: () => {
        if (this.isPlaying()) this.pause();
        else if (this.documentText && this.currentText === this.documentText) void this.play(); // resume
        else if (this.documentText) void this.playText(this.documentText);
      },
      isPlaying: () => this.isPlaying(),
    });
    this.documentReader = reader;

    let doc;
    try {
      doc = await this.apiClient.readDocument(url);
    } catch (error) {
      if (this.documentReader === reader) {
        const messages: Record<string, string> = {
          other_site: t('Only documents on this website can be read aloud.'),
          too_large: t('This document is too large to read (over 20 MB).'),
          not_pdf: t('This link isn’t a PDF.'),
          no_text: t('This PDF has no readable text (it may be a scanned image).'),
          unreadable: t('This PDF couldn’t be opened (it may be damaged or password-protected).'),
          not_found: t('This document couldn’t be found.'),
          rate_limited: t('Too many documents opened. Try again later.'),
        };
        const code = (error as { code?: string })?.code;
        reader.renderStatus((code && messages[code]) || t('This document couldn’t be read.'));
      }
      return;
    }
    if (this.documentReader !== reader) return; // closed while loading
    reader.render(doc);
    this.documentText = DocumentReader.readableText(doc);
    this.wordHighlighter?.setContentRoots([reader.contentElement]);
    this.showPlayingInMode();
    await this.playText(this.documentText);
  }

  private documentText: string | null = null;

  /** Close the reader view (if open), stop it and give highlighting back to the page */
  closeDocument(): void {
    if (!this.documentReader) return;
    const reader = this.documentReader;
    this.documentReader = null;
    this.documentText = null;
    this.stop();
    reader.destroy();
    this.wordHighlighter?.setContentRoots(this.textExtractor.getMatchedElements());
  }

  /** Load the toolbar/panel language (see i18n). Gives up after 2 s and stays in English. */
  private async loadUiStrings(): Promise<void> {
    // Always called (even for English) so Settings can switch language later
    const lang = resolveUiLanguage(this.config.uiLanguage);
    await Promise.race([
      loadUiLanguage(lang, UI_STRINGS_VERSION, (l) => this.apiClient.getUiStrings(l, UI_STRINGS_VERSION)),
      new Promise<void>((resolve) => setTimeout(resolve, 2000)),
    ]);
  }

  /** @internal API client (used by the Translation feature) */
  getApiClient(): APIClient {
    return this.apiClient;
  }

  /** Current reading language ('en' = original page text) */
  getLanguage(): string {
    return this.language;
  }

  /**
   * Set reading language for full-page playback. 'en' = original text with
   * word highlighting; anything else = machine-translated playback with the
   * translated sentence shown as a caption (the on-page text stays English).
   */
  setLanguage(code: string): void {
    const next = code || 'en';
    if (next === this.language) return;
    this.stop();
    this.disposeStreamingPlayer();
    this.language = next;
    this.emitCaption(null);
    this.notifyPlaybackEnd();
  }

  /** Element whose language decides the reading voice: the selection, else the first content block */
  private contentLanguageElement(): Element | null {
    const sel = typeof window !== 'undefined' ? window.getSelection() : null;
    const anchor = sel && !sel.isCollapsed ? sel.anchorNode : null;
    if (anchor) return anchor.nodeType === Node.ELEMENT_NODE ? (anchor as Element) : anchor.parentElement;
    return this.textExtractor?.getMatchedElements()[0] ?? null;
  }

  private splitForSpeech(text: string): string[] {
    return splitIntoSentences(text.replace(/\s+/g, ' ').trim());
  }

  /** Latest reading settings from the toolbar (applied when the highlighter is created) */
  private readingSettings: { autoScroll?: boolean; highlightMode?: string; highlightWordColor?: string; highlightSentenceColor?: string; volume?: number } = {};

  /**
   * Apply the visitor's reading settings: auto-scroll and highlight mode/colors.
   * Called by the modes on load and whenever Settings change.
   */
  applyReadingSettings(s: { autoScroll?: boolean; highlightMode?: string; highlightWordColor?: string; highlightSentenceColor?: string; volume?: number }): void {
    this.readingSettings = { ...this.readingSettings, ...s };
    const h = this.wordHighlighter;
    if (!h) return;
    if (typeof this.readingSettings.autoScroll === 'boolean') h.setAutoScroll(this.readingSettings.autoScroll);
    if (typeof this.readingSettings.volume === 'number') this.setVolume(this.readingSettings.volume / 100);
    h.setStyle({
      mode: this.readingSettings.highlightMode as any,
      word: this.readingSettings.highlightWordColor,
      sentence: this.readingSettings.highlightSentenceColor,
    });
  }

  /** Subscribe to translated-caption updates (null = clear) */
  onCaption(callback: (caption: string | null) => void): void {
    this.captionCallbacks.push(callback);
  }

  private emitCaption(caption: string | null): void {
    this.captionCallbacks.forEach(cb => {
      try { cb(caption); } catch { /* ignore */ }
    });
  }

  /**
   * Static factory method
   */
  static init(config: ReadingVoxConfig): ReadingVox {
    return new ReadingVox(config);
  }

  /** LMS detected on this page ('canvas' | 'd2l' | 'moodle' | 'blackboard' | null) */
  getDetectedLMS(): LmsId | null {
    return this.lms;
  }

  /** Page URL as sent to the API (without ?query/#hash when stripUrlParams is set) */
  getPageUrl(): string {
    return pageUrlForApi(!!this.config.stripUrlParams);
  }

  /** True when location.pathname matches config.excludePaths */
  isExcludedPage(): boolean {
    const paths = this.config.excludePaths;
    if (!paths || paths.length === 0) return false;
    const pathname = window.location.pathname || '/';
    return paths.some((p) => {
      try {
        return typeof p === 'string' ? new RegExp(p).test(pathname) : p.test(pathname);
      } catch {
        return false;
      }
    });
  }

  /** Content blocks currently being read (highlighter / extraction scope) */
  getContentBlocks(): Element[] {
    // An app that replaced its content container (new step, new route): find it again
    const roots = this.textExtractor.getMatchedElements();
    if (roots.length === 0 || roots.some((r) => !r.isConnected)) {
      const text = this.textExtractor.extract();
      if (this.currentText === this.pageText) this.currentText = text;
      this.pageText = text;
      this.wordHighlighter?.setContentRoots(this.textExtractor.getMatchedElements());
    }
    return this.textExtractor.getMatchedElements();
  }

  /**
   * Click-and-listen: read from the clicked paragraph (heading, list item…) to the
   * end of the content. Returns false if the element isn't in the readable content.
   */
  async readFrom(target: Element): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) return false;
    }
    const roots = this.getContentBlocks();
    if (!roots.some(r => r.contains(target))) return false;

    const BLOCKS = 'p, li, h1, h2, h3, h4, h5, h6, blockquote, td, th, dd, dt, figcaption, pre';
    const start = target.closest(BLOCKS) || target;
    // Leaf-level blocks across all content roots, in document order
    const blocks = roots
      .flatMap(r => Array.from(r.querySelectorAll(BLOCKS)))
      .filter(b => !b.querySelector(BLOCKS));
    const i = blocks.findIndex(b => b === start || b.contains(start) || start.contains(b));
    const chosen = i >= 0 ? blocks.slice(i) : [start];
    const text = chosen.map(b => this.textExtractor.textOf(b)).filter(t => t.trim()).join('\n\n');
    if (!text.trim()) return false;

    this.stop();
    this.showPlayingInMode();
    await this.playText(text);
    return true;
  }

  /** Is this node inside one of the content blocks? (contentOnly selection gating) */
  isInContent(node: Node | null): boolean {
    if (!node) return false;
    const el = node.nodeType === 1 ? (node as Element) : node.parentElement;
    if (!el) return false;
    if (el.closest('[contenteditable], textarea, input, [role="textbox"]')) return false;
    return this.textExtractor.getMatchedElements().some((root) => root.contains(el));
  }

  /**
   * Initialize the widget
   */
  async initialize(): Promise<void> {
    if (this.isInitialized || this.initStarted || this.destroyed) return;

    // Sensitive pages (e.g. Canvas grades, inbox): don't activate at all
    if (this.isExcludedPage()) {
      this.log('Page excluded by path — not activating:', window.location.pathname);
      return;
    }
    this.initStarted = true;

    try {
      // Inside an iframe of a page that also runs ReadingVox: be its agent instead
      if (this.config.readFrames !== false && await startFrameMode({
        frameOrigins: this.config.frameOrigins,
        ignoreSelectors: this.config.ignoreSelectors,
        debug: this.config.debug,
      })) {
        this.frameAgentMode = true;
        this.frames?.destroy(); // no nested frames under a frame agent
        this.frames = null;
        this.log('Frame mode: the parent page reads this frame');
        return;
      }
      if (this.config.readFrames !== false) {
        this.stopInviteListen = listenForInvite({ frameOrigins: this.config.frameOrigins }, () => this.joinParentLate());
      }

      // Initialize cache
      await this.cacheManager.init();


      // Extract text from page
      this.currentText = this.pageText = this.textExtractor.extract();

      if (this.config.observeContent) this.startContentObserver();

      if (!this.currentText || this.currentText.trim().length === 0) {
        if (this.config.observeContent) {
          // Content may be rendered later (React) — activate when it appears
          this.waitingForContent = true;
          this.log('No readable content yet — waiting for content blocks');
          return;
        }
        throw new Error('No readable text found on page');
      }

      await this.activate();
    } catch (error) {
      this.initStarted = false;
      console.error('[ReadingVox] Initialization error:', error);
      this.config.onError?.(error as Error);
      throw error;
    }
  }

  /** Build the UI for the configured mode (content has been found) */
  private async activate(): Promise<void> {
    this.log(`Extracted ${this.currentText.length} characters`);

    // Scope word highlighter to the same content roots used for extraction
    if (this.wordHighlighter) {
      this.wordHighlighter.setContentRoots(this.textExtractor.getMatchedElements());
    }

    // Interface language before any UI is built (never blocks for long)
    await this.loadUiStrings();

    // Fetch plan features on the visitor's first interaction (registered before the
    // UI exists, so a toolbar that's shown immediately also counts)
    document.addEventListener('rv-engaged', this.onEngaged);

    // Initialize based on mode
    switch (this.config.mode) {
      case 'page':
        // Page/block modes are loaded on demand in the website build (see lazy.ts)
        this.currentMode = new (await loadExtras()).PageMode(this);
        await this.currentMode.init();
        break;
      case 'block':
        this.currentMode = new (await loadExtras()).BlockMode(this);
        await this.currentMode.init();
        break;
      case 'highlight':
        this.currentMode = new HighlightMode(this);
        await this.currentMode.init();
        break;
    }

    if (this.destroyed) {
      this.currentMode?.destroy();
      this.currentMode = null;
      return;
    }

    this.isInitialized = true;
    this.listenButtons = new ListenButtons({
      playPage: () => this.playPage(),
      pause: () => this.pause(),
      pageState: () => this.pageState(),
      openToolbar: typeof (this.currentMode as any)?.openToolbar === 'function'
        ? () => (this.currentMode as any).openToolbar()
        : undefined,
    }, resolveTheme(this.config.theme));
    this.listenButtons.start();
    this.config.onReady?.();

    // Listen buttons on links to PDFs
    if (this.config.documentLinks === true) {
      this.stopDocumentLinks = watchDocumentLinks((url, button) => { void this.readDocument(url, button); });
    }

    if (this.lms) {
      this.whenFeatures().then((features) => this.handleLmsHint(features));
    }
  }

  private startContentObserver(): void {
    if (this.contentObserver) return;
    this.contentObserver = new ContentObserver(
      (root) => this.textExtractor.findBlocks(root),
      (blocks) => this.handleNewBlocks(blocks)
    );
    this.contentObserver.start();
  }

  /** Wire up content blocks added after load (no duplicates) */
  private handleNewBlocks(blocks: Element[]): void {
    if (this.destroyed) return;
    const added = this.textExtractor.addBlocks(blocks);
    if (added.length === 0) return;
    this.log(`Found ${added.length} new content block(s)`);

    const roots = this.textExtractor.getMatchedElements();
    if (this.wordHighlighter) this.wordHighlighter.setContentRoots(roots);

    if (this.waitingForContent) {
      const text = this.textExtractor.extractFrom();
      if (!text.trim()) return;
      this.waitingForContent = false;
      this.currentText = this.pageText = text;
      this.activate().catch((error) => {
        console.error('[ReadingVox] Initialization error:', error);
        this.config.onError?.(error as Error);
      });
      return;
    }

    if (!this.isPlaying()) {
      const readingPage = this.currentText === this.pageText;
      this.pageText = this.textExtractor.extractFrom();
      if (readingPage) this.currentText = this.pageText;
    }
    const mode = this.currentMode as any;
    if (mode && typeof mode.addContentBlocks === 'function') mode.addContentBlocks(added);
  }

  /**
   * Unlicensed-LMS hint from /widget/config. Soft enforcement: the widget keeps
   * working. Canvas / Moodle staff (teacher/admin) see a dismissible notice; students never do.
   */
  private handleLmsHint(features: WidgetFeatureConfig): void {
    const lms = features.lms;
    if (this.destroyed || !lms || !lms.detected || lms.licensed) return;
    const message = lms.message || 'ReadingVox is not licensed for use in this LMS.';

    const staff = (this.lms === 'canvas' && isCanvasStaff()) || (this.lms === 'moodle' && isMoodleStaff());
    if (staff) {
      loadExtras().then(({ LmsNotice }) => {
        if (this.destroyed || this.lmsNotice) return;
        this.lmsNotice = LmsNotice.show({
          lms: lms.detected as string,
          message,
          url: lms.url,
          togglePosition: this.config.togglePosition,
          toggleBottomOffset: this.config.toggleBottomOffset,
          theme: this.config.theme,
        });
      }).catch(() => { /* notice is best-effort */ });
    } else if (this.config.debug) {
      console.warn(`[ReadingVox] ${message}`);
    }
  }

  /**
   * Play text — always uses server TTS, falls back to browser on error
   */
  async playText(text: string): Promise<void> {
    void this.getFeatures();
    // Clear previous highlights before starting new playback
    if (this.wordHighlighter) {
      this.wordHighlighter.clearAll();
    }
    this.currentText = text;
    this.log('playText() — voice:', this.config.voice, 'speed:', this.config.speed, 'language:', this.language);

    if (this.language !== 'en') {
      await this.playTranslatedText(text, this.language);
      return;
    }

    // Read the content in its own language (page or element `lang`, or data-lang)
    const contentLang = this.config.contentLanguage && this.config.contentLanguage !== 'auto'
      ? this.config.contentLanguage
      : detectContentLanguage(this.contentLanguageElement());
    this.readingVoice = readingVoiceFor(contentLang, String(this.config.voice));
    if (this.readingVoice.kind === 'browser') {
      // No natural server voice for this language: the browser voice in that language,
      // with sentence highlighting (never an English voice reading another language)
      this.log('Content language', contentLang, '→ browser voice');
      const token = ++this.playbackToken;
      await this.speakWithBrowserLang(this.splitForSpeech(text), this.readingVoice.lang, token, false, true);
      return;
    }
    if (this.readingVoice.kind === 'natural') this.log('Content language', contentLang, '→', this.readingVoice.voice);

    try {
      await this.playWithServer();
      this.log('Server TTS stream completed');
    } catch (error) {
      this.log('Server TTS failed, falling back to browser:', error);
      await this.playWithBrowserTTS();
    }
  }

  /**
   * Translate text (split into sentences) and play it in the target language.
   * Uses the server voice for that language when available, otherwise browser speech.
   * No word highlighting — the translated sentence is emitted as a caption.
   */
  private async playTranslatedText(text: string, lang: string): Promise<void> {
    const token = ++this.playbackToken;
    const sentences = splitIntoSentences(text.replace(/\s+/g, ' ').trim());
    if (sentences.length === 0) return;

    const cacheKey = `${lang}\u0000${text}`;
    let result = this.translationCache.get(cacheKey);
    if (!result) {
      this.emitCaption(t('Translating…'));
      try {
        result = await this.apiClient.translateTexts(sentences, lang);
        this.translationCache.set(cacheKey, result);
      } catch (error) {
        if (token !== this.playbackToken) return;
        const status = (error as any)?.status;
        // Never show plan / quota wording to visitors
        const message = status === 402 || status === 403 || status === 401
          ? t("Translation isn't available right now.")
          : t('Translation failed. Please try again.');
        this.emitCaption(message);
        this.config.onError?.(error as Error);
        this.notifyPlaybackEnd();
        return;
      }
    }
    if (token !== this.playbackToken) return; // superseded by stop/new playback

    await this.speakTranslated(result.translations, result.voice, lang, token);
  }

  /**
   * Speak already-translated text (used by the selection-translation "Listen" button).
   */
  async speakTranslation(text: string, voice: string | null, lang: string): Promise<void> {
    const token = ++this.playbackToken;
    this.stop();
    this.playbackToken = token;
    await this.speakTranslated([text], voice, lang, token, false);
  }

  private async speakTranslated(
    sentences: string[],
    voice: string | null,
    lang: string,
    token: number,
    withCaption: boolean = true
  ): Promise<void> {
    if (this.wordHighlighter) this.wordHighlighter.clearAll();
    const joined = sentences.join(' ');

    if (voice) {
      try {
        await this.playWithServer({
          text: joined,
          voice,
          lang,
          onSentence: withCaption ? (s) => this.emitCaption(s) : undefined,
          oneOff: !withCaption,
        });
        return;
      } catch (error) {
        this.log('Server TTS for translation failed, using browser speech:', error);
        if (token !== this.playbackToken) return;
      }
    }

    await this.speakWithBrowserLang(sentences, lang, token, withCaption);
  }

  /**
   * Browser speech in a specific language (no server voice for that language)
   */
  private async speakWithBrowserLang(
    sentences: string[], lang: string, token: number, withCaption: boolean, highlightOnPage = false
  ): Promise<void> {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      this.notifyPlaybackEnd();
      return;
    }
    const synth = window.speechSynthesis;
    synth.cancel();
    this.translatedSpeechActive = true;

    for (const sentence of sentences) {
      if (token !== this.playbackToken || !this.translatedSpeechActive) break;
      if (withCaption) this.emitCaption(sentence);
      if (highlightOnPage) this.wordHighlighter?.highlightSentenceOnly(sentence);
      await new Promise<void>((resolve) => {
        const utterance = new SpeechSynthesisUtterance(sentence);
        utterance.lang = lang;
        utterance.rate = Math.max(0.5, Math.min(2, this.config.speed * 0.95));
        utterance.volume = getVolume();
        utterance.onend = () => resolve();
        utterance.onerror = () => resolve();
        synth.speak(utterance);
      });
    }

    if (token === this.playbackToken) {
      this.translatedSpeechActive = false;
      this.config.onEnd?.();
      this.notifyPlaybackEnd();
    }
  }

  private disposeStreamingPlayer(): void {
    if (this.streamingPlayer) {
      this.streamingPlayer.stop();
      this.streamingPlayer.destroy();
      this.streamingPlayer = null;
    }
  }

  /**
   * Play with browser TTS (instant, free)
   */
  private async playWithBrowserTTS(text: string = this.currentText): Promise<void> {
    this.browserTTSText = text;
    if (!this.browserTTS) {
      this.log('Browser TTS not available');
      return;
    }

    this.log('playWithBrowserTTS() — voice:', this.config.voice, 'speed:', this.config.speed);

    try {
      await this.browserTTS.speak(
        text,
        this.config.voice,
        this.config.speed
      );
    } catch (error) {
      console.error('[ReadingVox] Browser TTS error:', error);
      this.config.onError?.(error as Error);
    }

    // Speech finished — notify mode to reset player UI
    this.log('playWithBrowserTTS() finished — calling notifyPlaybackEnd');
    this.config.onEnd?.();
    this.notifyPlaybackEnd();
  }

  /**
   * Notify current mode that playback has ended so UI resets.
   * Skipped during speed/voice restart so the player doesn't flicker.
   */
  /** Find readable iframes and wire them into extraction, highlighting and selection */
  private startFrames(): void {
    if (this.frames) return;
    this.frames = new FrameManager({
      frameOrigins: this.config.frameOrigins,
      autoScroll: this.config.autoScroll !== false,
      ignoreSelectors: this.config.ignoreSelectors,
      debug: this.config.debug,
      onSelection: (text, rect) => this.showFrameSelection(text, rect),
      onSpeak: (text) => {
        this.stop();
        this.showPlayingInMode();
        void this.playText(text);
      },
    });
    this.textExtractor.frameText = this.frames.textFor;
    if (this.wordHighlighter instanceof FrameAwareHighlighter) this.wordHighlighter.frames = this.frames;
    this.frames.start();
  }

  /** Text selected inside a frame: offer the selection menu over it (empty text hides it) */
  private showFrameSelection(text: string, rect: FrameRect | null): void {
    const mode = this.currentMode as unknown as { showFrameSelection?: (t: string, r: FrameRect | null, clear: () => void) => void } | null;
    mode?.showFrameSelection?.(text, rect, () => this.frames?.clearSelections());
  }

  /** Click-and-listen on/off reaches readable frames too */
  setFrameClickListen(on: boolean): void {
    this.frames?.setClickListen(on);
  }

  /** Is this copy running as an agent inside a parent page's frame? */
  /**
   * The parent page's widget started after this frame gave up waiting and
   * invited it: drop this widget's own button and toolbar and let the parent
   * read the frame. Not while this widget is reading something.
   */
  private joinParentLate(): void {
    if (this.destroyed || this.isPlaying()) return;
    const { frameOrigins, ignoreSelectors, debug } = this.config;
    this.destroy();
    void startFrameMode({ frameOrigins, ignoreSelectors, debug }).then((joined) => {
      this.frameAgentMode = joined;
      if (joined) this.log('Frame mode (late): the parent page reads this frame');
    });
  }

  isFrameAgent(): boolean {
    return this.frameAgentMode;
  }

  /** Playback started from outside the toolbar (reader view, click-and-listen): show it there */
  private showPlayingInMode(): void {
    try {
      (this.currentMode as any)?.updateState?.({ isPlaying: true, progress: 0 });
    } catch { /* ignore */ }
  }

  private notifyPlaybackEnd(): void {
    if (this.isRestarting) {
      this.log('notifyPlaybackEnd() — skipped (restarting)');
      return;
    }
    this.log('notifyPlaybackEnd() — resetting UI');
    try {
      if (this.currentMode) {
        (this.currentMode as any).updateState({ isPlaying: false, progress: 0 });
      }
    } catch (e) {
      console.error('[ReadingVox] notifyPlaybackEnd error:', e);
    }
  }

  /**
   * Play with server TTS (Kokoro with word highlighting)
   */
  private async playWithServer(override?: {
    text: string;
    voice: string;
    /** Language of `text` (browser-voice fallback sentences use it) */
    lang?: string;
    onSentence?: (sentence: string) => void;
    oneOff?: boolean;
  }): Promise<void> {
    this.log('Playing with server TTS', override ? '(translated)' : '');
    const highlight = !override;
    this.streamingIsOneOff = !!override?.oneOff;
    const sourceText = override ? override.text : this.currentText;

    // Stop and destroy previous streaming player if it exists
    this.disposeStreamingPlayer();

    // Create fetch proxy for extension context (avoids CORS/Private Network Access issues)
    const fetchProxy = this.createFetchProxy();

    // Create streaming player
    this.streamingPlayer = new StreamingPlayer(this.cacheManager, {
      onPlay: (index) => {
        this.config.onPlay?.(index);
      },
      onPause: () => {
        this.config.onPause?.();
      },
      onEnd: () => {
        this.config.onEnd?.();
        if (override) {
          this.emitCaption(null);
          this.notifyPlaybackEnd();
        }
      },
      onSentenceStart: (index, sentence) => {
        this.log(`Sentence ${index}: ${sentence.substring(0, 50)}...`);
        override?.onSentence?.(sentence);
        // Non-English voices have no word timings: highlight the sentence instead
        if (highlight && this.streamingPlayer && !this.streamingPlayer.hasWordTimings(index)) {
          this.wordHighlighter?.highlightSentenceOnly(sentence);
        }
      },
      onWordHighlight: (info) => {
        if (!highlight) return; // translated audio doesn't match on-page text
        if (this.wordHighlighter) {
          this.wordHighlighter.highlightWord(info);
        }
        this.config.onWordHighlight?.(info.word, info.timestamp);
        // Emit to toolbar tools (magnifier, screen mask, etc.)
        const sentenceWords = (info.sentence || '').split(/\s+/);
        const wordIdx = sentenceWords.indexOf(info.word);
        this.emitWordHighlight({
          word: info.word,
          sentence: info.sentence || '',
          wordIndex: wordIdx >= 0 ? wordIdx : 0,
          timestamp: info.timestamp,
        });
      },
      onError: (error) => {
        this.config.onError?.(error);
      },
      onBuffering: (buffering) => {
        this.buffering = buffering;
        const mode = this.currentMode as any;
        if (mode && typeof mode.setBuffering === 'function') mode.setBuffering(buffering);
      },
      onStreamFailed: (nextIndex) => {
        if (this.streamingPlayer !== player) return;
        if (override) {
          this.emitCaption(null);
          this.notifyPlaybackEnd();
          return;
        }
        // Continue the rest of the text with browser speech
        const remaining = this.remainingTextAfter(sourceText, nextIndex, player);
        this.log(`Sentence stream failed at ${nextIndex} — browser speech for the remaining ${remaining.length} chars`);
        this.disposeStreamingPlayer();
        if (remaining) {
          this.playWithBrowserTTS(remaining);
        } else {
          this.config.onEnd?.();
          this.notifyPlaybackEnd();
        }
      },
    }, fetchProxy, this.createFallbackSpeaker(
      override ? (override.lang || 'en') : this.readingVoice.kind === 'natural' ? this.readingVoice.lang : null
    ));
    const player = this.streamingPlayer;
    player.setSpeed(this.config.speed);

    // Generate the text a window at a time, just ahead of the listener: quota is
    // only used for what's actually reached, and the server pre-warms the rest
    // of the page for free. An older server ignores the window and sends all.
    const natural = this.readingVoice.kind === 'natural' ? this.readingVoice : null;
    const voice = override ? override.voice : natural ? natural.voice : this.config.voice;
    const superseded = () => this.streamingPlayer !== player;
    let start = 0;

    try {
      for (;;) {
        const win: { current: StreamWindow | null } = { current: null };
        const sentenceStream = this.apiClient.streamSentences(sourceText, voice, this.getPageUrl(), {
          start,
          limit: STREAM_WINDOW,
          onWindow: (w) => { win.current = w; },
        });
        for await (const sentence of sentenceStream) {
          if (superseded()) break;
          await player.addSentence(sentence);
          this.log(`Received sentence ${sentence.index}: ${sentence.cached ? 'CACHED' : 'GENERATED'}`);
        }
        if (superseded()) return;

        const w = win.current;
        if (!w || w.next >= w.total || w.next <= start) break; // all sent

        // Ask for the next window once playback is within STREAM_LOOKAHEAD sentences of it
        while (!superseded() && player.getCurrentIndex() < w.next - STREAM_LOOKAHEAD) {
          await new Promise((r) => setTimeout(r, 300));
        }
        if (superseded()) return;
        start = w.next;
      }
    } catch (error) {
      // Nothing received yet → let playText() fall back to browser speech for everything
      if (superseded()) throw error;
      if (player.receivedCount() === 0) {
        // Drop the empty player so pause/stop/speed reach the browser voice
        this.disposeStreamingPlayer();
        throw error;
      }
      this.log('Sentence stream failed mid-way:', error);
      player.markStreamFailed();
      return;
    }

    player.markStreamComplete();
    this.log('All sentences received');
  }

  /**
   * Browser voice for sentences the server sends with `fallback: "browser"`
   * (over the monthly limit, uncached). Original-language playback uses
   * BrowserTTS so its boundary events drive word highlighting; translated
   * playback speaks in `lang` without highlighting. Undefined when the browser
   * has no speechSynthesis (those sentences are then skipped).
   */
  private createFallbackSpeaker(lang: string | null): FallbackSpeaker | undefined {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return undefined;

    if (lang === null) {
      const tts = this.browserTTS;
      if (!tts) return undefined;
      return {
        speak: (text, speed) => tts.speak(text, this.config.voice, speed),
        stop: () => tts.stop(),
      };
    }

    const synth = window.speechSynthesis;
    let finish: (() => void) | null = null;
    return {
      speak: (text, speed) => new Promise<void>((resolve) => {
        synth.cancel();
        finish?.();
        let done = false;
        const end = () => { if (!done) { done = true; finish = null; resolve(); } };
        finish = end;
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = lang;
        utterance.rate = Math.max(0.5, Math.min(2, speed * 0.95));
        utterance.volume = getVolume();
        utterance.onend = end;
        utterance.onerror = end;
        synth.speak(utterance);
      }),
      stop: () => { const f = finish; finish = null; synth.cancel(); f?.(); },
    };
  }

  /**
   * Text that follows the sentences the server delivered before `nextIndex`
   * (best effort: locates each received sentence in order within the text).
   */
  private remainingTextAfter(text: string, nextIndex: number, player: StreamingPlayer): string {
    let pos = 0;
    for (let i = 0; i < nextIndex; i++) {
      const sentence = player.getSentenceText(i);
      if (!sentence) continue;
      const at = text.indexOf(sentence.trim(), pos);
      if (at >= 0) pos = at + sentence.trim().length;
    }
    return text.slice(pos).trim();
  }

  /**
   * Create a fetch proxy that routes through the extension service worker.
   * Only used when running inside a Chrome extension with a loopback API endpoint,
   * to avoid Private Network Access (PNA) CORS blocks.
   */
  private createFetchProxy(): ((url: string, responseType: 'blob' | 'json') => Promise<any>) | undefined {
    const ext = (globalThis as any).chrome;
    if (!ext?.runtime?.sendMessage) return undefined;

    // Only proxy loopback URLs
    try {
      const hostname = new URL(this.config.apiEndpoint || '').hostname.toLowerCase();
      if (hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.endsWith('.localhost')) {
        return undefined;
      }
    } catch {
      return undefined;
    }

    this.log('Using extension fetch proxy for loopback audio');
    return (url: string, responseType: 'blob' | 'json') =>
      ext.runtime.sendMessage({ type: 'PROXY_FETCH', url, responseType });
  }

  /**
   * Download the page's audio as one MP3. Generates any sentences that aren't
   * cached yet (counts toward the site's allowance like listening does), then
   * joins the per-sentence MP3s — same encoding, so frames concatenate cleanly.
   */
  async downloadAudio(onProgress?: (done: number, total: number) => void): Promise<{ ok: boolean; message: string }> {
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) return { ok: false, message: t('Nothing to download on this page.') };
    }
    const sel = typeof window !== 'undefined' ? readableSelectionText() : '';
    const text = sel || this.textExtractor.extract();
    if (!text) return { ok: false, message: t('Nothing to download on this page.') };

    const contentLang = this.config.contentLanguage && this.config.contentLanguage !== 'auto'
      ? this.config.contentLanguage
      : detectContentLanguage(this.contentLanguageElement());
    const reading = readingVoiceFor(contentLang, String(this.config.voice));
    if (reading.kind === 'browser') {
      return { ok: false, message: t("Audio download isn't available for this page's language.") };
    }
    const voice = reading.kind === 'natural' ? reading.voice : String(this.config.voice);

    const urls: string[] = [];
    let missing = 0;
    let total = 0;
    try {
      for await (const s of this.apiClient.streamSentences(text, voice, this.getPageUrl())) {
        total = Math.max(total, s.index + 1);
        if (s.audioUrl) urls[s.index] = s.audioUrl;
        else missing++;
        onProgress?.(urls.filter(Boolean).length + missing, total);
      }
    } catch {
      return { ok: false, message: t("Couldn't prepare the audio. Please try again.") };
    }

    const fetchProxy = this.createFetchProxy();
    const parts: Blob[] = [];
    for (const url of urls) {
      if (!url) continue;
      try {
        if (fetchProxy) {
          const r = await fetchProxy(url, 'blob');
          if (r?.ok && r.dataUrl) parts.push(await (await fetch(r.dataUrl)).blob());
        } else {
          const r = await fetch(url);
          if (r.ok) parts.push(await r.blob());
        }
      } catch { /* skip this sentence */ }
    }
    if (parts.length === 0) return { ok: false, message: t("Audio isn't available for this page right now.") };

    const blob = new Blob(parts, { type: 'audio/mpeg' });
    const name = (document.title || 'readingvox-audio').replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 80) || 'readingvox-audio';
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `${name}.mp3`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(href), 60_000);

    const skipped = total - parts.length;
    return {
      ok: true,
      message: skipped > 0 ? t("Downloaded ({n} sentences couldn't be included).", { n: skipped }) : t('Downloaded.'),
    };
  }

  /**
   * AI summary of the selection (or the page): plain-language bullet points.
   * Returns a visitor-facing message instead of bullets when unavailable.
   */
  async summarize(): Promise<{ ok: boolean; bullets?: string[]; message?: string }> {
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) return { ok: false, message: t('Nothing to summarize on this page.') };
    }
    const sel = typeof window !== 'undefined' ? readableSelectionText() : '';
    const text = sel || this.textExtractor.extract();
    if (!text || text.length < 200) return { ok: false, message: t("There isn't enough text here to summarize.") };
    try {
      return { ok: true, bullets: await this.apiClient.summarize(text) };
    } catch (error: any) {
      const status = error?.status;
      return { ok: false, message: status === 400 ? t("There isn't enough text here to summarize.") : t("Summary isn't available right now.") };
    }
  }

  /** Speak short text that isn't on the page (form labels, summaries) with the reading voice */
  async speakText(text: string): Promise<void> {
    await this.speakSummary([text]);
  }

  /** Read a summary aloud (it isn't on the page, so no highlighting) */
  async speakSummary(bullets: string[]): Promise<void> {
    const lang = this.config.contentLanguage && this.config.contentLanguage !== 'auto'
      ? this.config.contentLanguage
      : detectContentLanguage(this.contentLanguageElement());
    const reading = readingVoiceFor(lang, String(this.config.voice));
    const voice = reading.kind === 'natural' ? reading.voice : reading.kind === 'browser' ? null : String(this.config.voice);
    await this.speakTranslation(bullets.join(' '), voice, reading.kind === 'default' ? 'en' : reading.lang);
  }

  /** Playback volume 0–1 (server audio and browser speech) */
  setVolume(v: number): void {
    setVolumeLevel(v);
    this.streamingPlayer?.applyVolume();
  }

  /** Progress through the current playback, 0–100 */
  getProgress(): number {
    return this.streamingPlayer ? this.streamingPlayer.getProgress() : 0;
  }

  /** Skip to the next (+1) or previous (−1) sentence during playback */
  skipSentence(delta: number): void {
    this.streamingPlayer?.skip(delta);
  }

  /**
   * Read the whole page (the placed Listen buttons): resumes it if paused,
   * otherwise starts it from the top, replacing a selection or translation.
   */
  async playPage(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) return;
    }
    const state = this.pageState().state;
    if (state === 'playing' || state === 'loading') return;
    if (state === 'paused' && !this.streamingPlayer && this.browserTTS) {
      this.browserTTS.resume();
      this.showPlayingInMode();
      return;
    }
    if (state !== 'paused') {
      this.stop();
      this.disposeStreamingPlayer();
      this.refreshPageText();
      this.currentText = this.pageText;
    }
    this.showPlayingInMode();
    await this.play();
  }

  /** Is the page (not a selection or translation clip) loading, playing or paused? */
  pageState(): { state: PageListenState; progress: number } {
    if (!this.pageText || this.currentText !== this.pageText || this.translatedSpeechActive) return { state: 'idle', progress: 0 };
    if (this.streamingPlayer) {
      if (this.streamingIsOneOff) return { state: 'idle', progress: 0 };
      const progress = this.streamingPlayer.getProgress();
      if (this.streamingPlayer.isActive()) return { state: this.buffering && progress === 0 ? 'loading' : 'playing', progress };
      if (this.streamingPlayer.isPausedState()) return { state: 'paused', progress };
      return { state: 'idle', progress: 0 };
    }
    if (this.browserTTS?.isSpeaking()) return { state: this.browserTTS.isPausedState() ? 'paused' : 'playing', progress: 0 };
    return { state: 'idle', progress: 0 };
  }

  /**
   * Play
   */
  async play(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
      if (!this.isInitialized) return; // excluded page / no content yet
    }

    this.log('play() — currentText length:', this.currentText.length);

    // Don't "resume" a one-off translation clip when the user presses Play
    if (this.streamingPlayer && this.streamingIsOneOff) {
      this.disposeStreamingPlayer();
    }

    // The page can change without a reload (step-by-step quizzes, single-page
    // apps): read what's on it now, not what was there when the widget started
    if (this.currentText === this.pageText) this.refreshPageText();

    if (this.streamingPlayer) {
      await this.streamingPlayer.play();
    } else {
      await this.playText(this.currentText);
    }
  }

  /**
   * Re-read the page's content. If it changed since the last read (a new quiz
   * step, another route), the old player is dropped so Play starts the new
   * content instead of resuming the old one.
   */
  private refreshPageText(): void {
    const fresh = this.textExtractor.extract();
    if (!fresh.trim() || fresh === this.pageText) return;
    this.log('Page content changed — reading the current content');
    this.disposeStreamingPlayer();
    this.wordHighlighter?.clearAll();
    this.wordHighlighter?.setContentRoots(this.textExtractor.getMatchedElements());
    this.currentText = this.pageText = fresh;
  }

  /**
   * Pause
   */
  pause(): void {
    if (this.translatedSpeechActive) {
      // Browser speech in another language can't reliably resume — stop it
      this.stopTranslatedSpeech();
    } else if (this.streamingPlayer) {
      this.streamingPlayer.pause();
    } else if (this.browserTTS) {
      this.browserTTS.pause();
    }

    this.config.onPause?.();
  }

  /**
   * Stop
   */
  stop(): void {
    this.playbackToken++;
    if (this.translatedSpeechActive) this.stopTranslatedSpeech();
    if (this.language !== 'en') this.emitCaption(null);
    if (this.streamingPlayer) {
      this.streamingPlayer.stop();
    } else if (this.browserTTS) {
      this.browserTTS.stop();
    }
    // Clear word/sentence highlights
    if (this.wordHighlighter) {
      this.wordHighlighter.clearAll();
    }
  }

  private stopTranslatedSpeech(): void {
    this.translatedSpeechActive = false;
    this.playbackToken++;
    try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
  }

  /**
   * Toggle play/pause
   */
  togglePlayPause(): void {
    if (this.isPlaying()) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Seek to a percentage (0-100)
   */
  seek(percent: number): void {
    if (this.streamingPlayer) {
      this.streamingPlayer.seek(percent);
    }
  }

  /**
   * Set speed — restarts browser TTS if currently speaking (rate can't change mid-utterance)
   */
  setSpeed(speed: number): void {
    this.config.speed = Math.max(0.5, Math.min(2.0, speed));
    this.log('setSpeed()', this.config.speed);

    if (this.streamingPlayer) {
      // Also restarts a browser-voice (fallback) sentence at the new rate
      this.streamingPlayer.setSpeed(this.config.speed);
      return;
    }

    // Web Speech API: rate can only be set before speak(), not during.
    // If currently speaking with browser TTS, restart with new speed.
    // isSpeaking() uses our manual flag, not Chrome's stale synth.speaking
    if (this.browserTTS && this.browserTTS.isSpeaking()) {
      this.log('setSpeed() — restarting active speech');
      this.restartBrowserTTS();
    }
  }

  /**
   * Set voice — restarts browser TTS if currently speaking
   */
  setVoice(voice: string): void {
    this.config.voice = normalizeVoice(voice) as any;
    this.log('setVoice()', voice);

    // If currently speaking with browser TTS, restart with new voice
    // (not for a fallback sentence inside server playback — the next one picks it up)
    if (!this.streamingPlayer && this.browserTTS && this.browserTTS.isSpeaking()) {
      this.log('setVoice() — restarting active speech');
      this.restartBrowserTTS();
    }
  }

  /**
   * Stop and restart browser TTS with current config.
   * Skips the notifyPlaybackEnd from the cancelled utterance.
   */
  private restartBrowserTTS(): void {
    if (!this.browserTTS) return;
    this.isRestarting = true;
    this.browserTTS.stop();
    // Small delay so the cancelled utterance resolves first
    setTimeout(() => {
      this.isRestarting = false;
      this.playWithBrowserTTS(this.browserTTSText || this.currentText);
    }, 10);
  }

  /**
   * Check if playing
   */
  isPlaying(): boolean {
    if (this.translatedSpeechActive) return true;
    if (this.streamingPlayer) {
      return this.streamingPlayer.isActive();
    } else if (this.browserTTS) {
      return this.browserTTS.isSpeaking();
    }
    return false;
  }

  /**
   * Register a callback for word highlight events (used by TextMagnifier, ScreenMask)
   */
  onWordHighlight(callback: (event: WordHighlightEvent) => void): void {
    this.wordHighlightCallbacks.push(callback);
  }

  /**
   * Emit word highlight event to all listeners
   */
  private emitWordHighlight(event: WordHighlightEvent): void {
    this.wordHighlightCallbacks.forEach(cb => cb(event));
  }

  /**
   * Destroy widget
   */
  destroy(): void {
    this.destroyed = true;
    this.stop();

    this.contentObserver?.stop();
    this.contentObserver = null;
    this.frames?.destroy();
    this.frames = null;
    this.stopInviteListen?.();
    this.stopInviteListen = null;
    this.listenButtons?.destroy();
    this.listenButtons = null;
    this.stopDocumentLinks?.();
    this.stopDocumentLinks = null;
    document.removeEventListener('rv-engaged', this.onEngaged);
    this.closeDocument();
    this.lmsNotice?.destroy();
    this.lmsNotice = null;

    if (this.streamingPlayer) {
      this.streamingPlayer.destroy();
    }

    if (this.wordHighlighter) {
      this.wordHighlighter.destroy();
    }

    if (this.currentMode) {
      this.currentMode.destroy();
    }

    this.wordHighlightCallbacks = [];
    this.captionCallbacks = [];
    this.isInitialized = false;
    ReadingVox.instances.delete(this);
  }

  /**
   * Get cache statistics
   */
  async getCacheStats() {
    return await this.cacheManager.getStats();
  }

  /**
   * Clear cache
   */
  async clearCache() {
    await this.cacheManager.clear();
  }

  /**
   * Log debug message
   */
  private log(...args: any[]) {
    if (this.config.debug) {
      console.log('[ReadingVox]', ...args);
    }
  }
}

// Export for global window access
if (typeof window !== 'undefined') {
  (window as any).ReadingVox = ReadingVox;
}
